import { supabase } from '@/integrations/supabase/client';

export interface LocationPoint {
  latitude: number;
  longitude: number;
}

export interface DisplaySearchResult {
  id: string;
  name: string;
  location: string;
  latitude: number;
  longitude: number;
  distance_meters: number;
  holiday_type: string | null;
  display_type: string | null;
  rating: number;
  year_started?: number;
}

export interface RegionStats {
  total_displays: number;
  avg_rating: number;
  holiday_types: string[];
  display_types: string[];
}

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  address: string;
  confidence: number;
}

export interface LocationSearchOptions {
  radius?: number; // in meters
  limit?: number;
  minRating?: number;
  holidayTypes?: string[];
  displayTypes?: string[];
  sortBy?: 'distance' | 'rating' | 'name';
}

export interface LocationSearchFilters {
  searchText?: string;
  location?: LocationPoint;
  radius?: number;
  holidayType?: string[];
  displayType?: string[];
  minRating?: number;
  maxResults?: number;
}

/**
 * Core LocationService class for geospatial operations
 */
export class LocationService {
  private static cache = new Map<string, { data: any; timestamp: number }>();
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private static getCacheKey(operation: string, params: any): string {
    return `${operation}_${JSON.stringify(params)}`;
  }

  private static getCachedResult<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data as T;
    }
    if (cached) {
      this.cache.delete(key);
    }
    return null;
  }

  private static setCachedResult(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Find displays within a specific radius from a location
   */
  static async findDisplaysWithinRadius(
    center: LocationPoint,
    options: LocationSearchOptions = {}
  ): Promise<DisplaySearchResult[]> {
    const { radius = 50000, limit = 50, minRating, holidayTypes, displayTypes, sortBy = 'distance' } = options;
    const cacheKey = this.getCacheKey('radius_search', { center, radius, limit, minRating, holidayTypes, displayTypes, sortBy });
    
    // Check cache first
    const cached = this.getCachedResult<DisplaySearchResult[]>(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase.rpc('find_displays_within_radius', {
        center_lat: center.latitude,
        center_lng: center.longitude,
        radius_meters: radius
      });

      if (error) {
        console.error('Error finding displays within radius:', error);
        throw new Error(`Geospatial search failed: ${error.message}`);
      }

      let results = data || [];

      // Apply additional filters
      if (minRating) {
        results = results.filter((d: any) => d.rating >= minRating);
      }
      
      if (holidayTypes && holidayTypes.length > 0) {
        results = results.filter((d: any) => 
          d.holiday_type && holidayTypes.includes(d.holiday_type)
        );
      }
      
      if (displayTypes && displayTypes.length > 0) {
        results = results.filter((d: any) => 
          d.display_type && displayTypes.includes(d.display_type)
        );
      }

      // Apply sorting
      if (sortBy === 'rating') {
        results.sort((a: any, b: any) => b.rating - a.rating);
      } else if (sortBy === 'name') {
        results.sort((a: any, b: any) => a.name.localeCompare(b.name));
      }
      // 'distance' is already sorted by the database function

      // Apply limit
      if (limit) {
        results = results.slice(0, limit);
      }

      // Transform to match interface
      const transformedResults: DisplaySearchResult[] = results.map((d: any) => ({
        id: d.id.toString(),
        name: d.name,
        location: d.location,
        latitude: d.latitude,
        longitude: d.longitude,
        distance_meters: d.distance_meters,
        holiday_type: d.holiday_type,
        display_type: d.display_type,
        rating: d.rating || 0,
        year_started: d.year_started
      }));

      this.setCachedResult(cacheKey, transformedResults);
      return transformedResults;
    } catch (error) {
      console.error('Error in findDisplaysWithinRadius:', error);
      throw error;
    }
  }

  /**
   * Find the nearest displays to a location
   */
  static async findNearestDisplays(
    center: LocationPoint,
    limit: number = 20
  ): Promise<DisplaySearchResult[]> {
    const cacheKey = this.getCacheKey('nearest_displays', { center, limit });
    
    const cached = this.getCachedResult<DisplaySearchResult[]>(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase.rpc('find_nearest_displays', {
        center_lat: center.latitude,
        center_lng: center.longitude,
        limit_count: limit
      });

      if (error) {
        console.error('Error finding nearest displays:', error);
        throw new Error(`Nearest search failed: ${error.message}`);
      }

      const results: DisplaySearchResult[] = (data || []).map((d: any) => ({
        id: d.id.toString(),
        name: d.name,
        location: d.location,
        latitude: d.latitude,
        longitude: d.longitude,
        distance_meters: d.distance_meters,
        holiday_type: d.holiday_type,
        display_type: d.display_type,
        rating: d.rating || 0
      }));

      this.setCachedResult(cacheKey, results);
      return results;
    } catch (error) {
      console.error('Error in findNearestDisplays:', error);
      throw error;
    }
  }

  /**
   * Search displays by location text with fuzzy matching
   */
  static async searchDisplaysByLocation(
    searchText: string,
    radius: number = 25000
  ): Promise<DisplaySearchResult[]> {
    const cacheKey = this.getCacheKey('location_text_search', { searchText, radius });
    
    const cached = this.getCachedResult<DisplaySearchResult[]>(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase.rpc('search_displays_by_location', {
        search_text: searchText,
        radius_meters: radius
      });

      if (error) {
        console.error('Error searching displays by location:', error);
        throw new Error(`Location text search failed: ${error.message}`);
      }

      const results: DisplaySearchResult[] = (data || []).map((d: any) => ({
        id: d.id.toString(),
        name: d.name,
        location: d.location,
        latitude: d.latitude,
        longitude: d.longitude,
        distance_meters: d.distance_meters,
        holiday_type: d.holiday_type,
        display_type: d.display_type,
        rating: d.rating || 0
      }));

      this.setCachedResult(cacheKey, results);
      return results;
    } catch (error) {
      console.error('Error in searchDisplaysByLocation:', error);
      throw error;
    }
  }

  /**
   * Get statistics for displays in a region
   */
  static async getRegionStats(
    center: LocationPoint,
    radius: number = 100000
  ): Promise<RegionStats> {
    const cacheKey = this.getCacheKey('region_stats', { center, radius });
    
    const cached = this.getCachedResult<RegionStats>(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase.rpc('get_display_stats_by_region', {
        center_lat: center.latitude,
        center_lng: center.longitude,
        radius_meters: radius
      });

      if (error) {
        console.error('Error getting region stats:', error);
        throw new Error(`Region stats failed: ${error.message}`);
      }

      const result = data?.[0] || {
        total_displays: 0,
        avg_rating: 0,
        holiday_types: [],
        display_types: []
      };

      const stats: RegionStats = {
        total_displays: parseInt(result.total_displays) || 0,
        avg_rating: parseFloat(result.avg_rating) || 0,
        holiday_types: result.holiday_types || [],
        display_types: result.display_types || []
      };

      this.setCachedResult(cacheKey, stats);
      return stats;
    } catch (error) {
      console.error('Error in getRegionStats:', error);
      throw error;
    }
  }

  /**
   * Geocode an address to coordinates using Mapbox Geocoding API
   */
  static async geocodeAddress(address: string): Promise<GeocodeResult | null> {
    const cacheKey = this.getCacheKey('geocode', { address });
    
    const cached = this.getCachedResult<GeocodeResult>(cacheKey);
    if (cached) return cached;

    try {
      // Try to extract coordinates if they're already in the address
      const coordMatch = address.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
      if (coordMatch) {
        const result: GeocodeResult = {
          latitude: parseFloat(coordMatch[1]),
          longitude: parseFloat(coordMatch[2]),
          address: address,
          confidence: 1.0
        };
        this.setCachedResult(cacheKey, result);
        return result;
      }

      // Use Mapbox Geocoding API
      const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoiZnJpZmViIiwiYSI6ImNtOHV5ZnJsazBjMmEya3B5aXUwZThyeTYifQ.h2ktDA_en2v7BOTJ4LrdYQ';
      
      if (!mapboxToken || mapboxToken === 'your_mapbox_access_token') {
        console.warn('Mapbox token not configured for geocoding');
        return null;
      }

      // Clean and encode the address
      const cleanAddress = address.trim();
      const encodedAddress = encodeURIComponent(cleanAddress);
      
      // Mapbox Geocoding API endpoint
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxToken}&limit=1&country=us&types=address,poi`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error(`Mapbox geocoding failed: ${response.status} ${response.statusText}`);
        return null;
      }
      
      const data = await response.json();
      
      if (!data.features || data.features.length === 0) {
        console.log(`No geocoding results found for address: ${address}`);
        return null;
      }
      
      const feature = data.features[0];
      const [longitude, latitude] = feature.center;
      
      const result: GeocodeResult = {
        latitude: latitude,
        longitude: longitude,
        address: feature.place_name || address,
        confidence: feature.relevance || 0.5
      };
      
      this.setCachedResult(cacheKey, result);
      return result;
      
    } catch (error) {
      console.error('Error geocoding address:', error);
      return null;
    }
  }

  /**
   * Get user's current location using browser geolocation
   */
  static async getCurrentLocation(): Promise<LocationPoint | null> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting current location:', error);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  }

  /**
   * Calculate distance between two points in meters
   */
  static calculateDistance(point1: LocationPoint, point2: LocationPoint): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = point1.latitude * Math.PI / 180;
    const φ2 = point2.latitude * Math.PI / 180;
    const Δφ = (point2.latitude - point1.latitude) * Math.PI / 180;
    const Δλ = (point2.longitude - point1.longitude) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  }

  /**
   * Format distance for display
   */
  static formatDistance(meters: number): string {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    } else if (meters < 10000) {
      return `${(meters / 1000).toFixed(1)}km`;
    } else {
      return `${Math.round(meters / 1000)}km`;
    }
  }

  /**
   * Calculate similarity score between two addresses (0-100)
   * Uses fuzzy string matching for duplicate detection
   */
  static calculateAddressSimilarity(address1: string, address2: string): number {
    if (!address1 || !address2) return 0;
    
    // Normalize addresses (lowercase, remove extra spaces, common abbreviations)
    const normalize = (addr: string): string => {
      return addr
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/\bstreet\b/g, 'st')
        .replace(/\bavenue\b/g, 'ave')
        .replace(/\broads?\b/g, 'rd')
        .replace(/\bdrive\b/g, 'dr')
        .replace(/\bcourt\b/g, 'ct')
        .replace(/\blane\b/g, 'ln')
        .replace(/\bplace\b/g, 'pl')
        .replace(/\bboulevard\b/g, 'blvd')
        .replace(/\bapartment\b/g, 'apt')
        .replace(/\bsuite\b/g, 'ste')
        .replace(/[^\w\s]/g, '')
        .trim();
    };
    
    const norm1 = normalize(address1);
    const norm2 = normalize(address2);
    
    if (norm1 === norm2) return 100;
    
    // Calculate Levenshtein distance-based similarity
    const maxLength = Math.max(norm1.length, norm2.length);
    if (maxLength === 0) return 100;
    
    const distance = this.levenshteinDistance(norm1, norm2);
    const similarity = ((maxLength - distance) / maxLength) * 100;
    
    return Math.round(similarity);
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  private static levenshteinDistance(str1: string, str2: string): number {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,     // deletion
          matrix[j - 1][i] + 1,     // insertion
          matrix[j - 1][i - 1] + indicator // substitution
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  /**
   * Find potential duplicate displays by address similarity
   */
  static async findPotentialDuplicates(
    targetAddress: string,
    threshold: number = 85
  ): Promise<Array<{ id: string; address: string; similarity: number }>> {
    try {
      // Get all displays with addresses
      const { data: displays, error } = await supabase
        .from('displays')
        .select('id, location')
        .not('location', 'is', null);
      
      if (error) {
        console.error('Error fetching displays for duplicate check:', error);
        return [];
      }
      
      const matches: Array<{ id: string; address: string; similarity: number }> = [];
      
      for (const display of displays || []) {
        const similarity = this.calculateAddressSimilarity(targetAddress, display.location);
        if (similarity >= threshold) {
          matches.push({
            id: display.id.toString(),
            address: display.location,
            similarity
          });
        }
      }
      
      // Sort by similarity score (highest first)
      return matches.sort((a, b) => b.similarity - a.similarity);
      
    } catch (error) {
      console.error('Error finding potential duplicates:', error);
      return [];
    }
  }

  /**
   * Clear the cache (useful for testing or manual cache invalidation)
   */
  static clearCache(): void {
    this.cache.clear();
  }
}

// Export default functions for convenience
export const findDisplaysWithinRadius = LocationService.findDisplaysWithinRadius.bind(LocationService);
export const findNearestDisplays = LocationService.findNearestDisplays.bind(LocationService);
export const searchDisplaysByLocation = LocationService.searchDisplaysByLocation.bind(LocationService);
export const getRegionStats = LocationService.getRegionStats.bind(LocationService);
export const geocodeAddress = LocationService.geocodeAddress.bind(LocationService);
export const getCurrentLocation = LocationService.getCurrentLocation.bind(LocationService);
export const calculateDistance = LocationService.calculateDistance.bind(LocationService);
export const formatDistance = LocationService.formatDistance.bind(LocationService); 